import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Grid, Button, Typography, Box } from '@mui/material'
import { translate } from '../i18n'
import { enable2FA, confirm2FA, update2FA } from '../api'
import { User, DoubleFactorAuthenticationRequest } from '../model'
import { Code2FAValidator } from '../validators'
import { FeedbackContext } from '../feedback'
import { AnyInput, useValidatedForm } from '../anyform'
import { Surface, TwoFALockIcon } from '../components'
import { AppContext } from '../context'

export default function User2FA() {
    const feedbackContext = useContext(FeedbackContext)
    const context = useContext(AppContext)
    const sessionUser = context.session?.user

    const validators = useMemo(() => [new Code2FAValidator()], [])
    const { register, bind, isValid, setPristine } =
        useValidatedForm<DoubleFactorAuthenticationRequest>()
    const [user, setUser] = useState<User>(sessionUser!)

    const updateUser = useCallback(
        (user: User) => {
            if (!context.session) return

            const curr = context.session.user
            const newUser = {
                ...curr,
                status_2fa: user.status_2fa,
                qr_url_2fa: user.qr_url_2fa,
                secret_2fa: user.secret_2fa,
            }

            context.session.user = newUser
            setUser(newUser)
        },
        [context.session]
    )

    const cleanForm = useCallback(
        (user: User) => {
            setPristine()
            updateUser(user)
        },
        [setPristine, updateUser]
    )

    const onEnable2FA = useCallback(() => {
        feedbackContext.showBackdrop()
        enable2FA()
            .then((user) => {
                feedbackContext.showSuccess(
                    translate('users.security.enabling_2fa') as string
                )
                cleanForm(user)
            })
            .catch((error) => feedbackContext.showError(error.message))
            .finally(feedbackContext.closeBackdrop)
    }, [cleanForm, feedbackContext])

    const onConfirm2FA = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            if (!isValid()) {
                return
            }

            feedbackContext.showBackdrop()

            const request = bind()
            confirm2FA(request)
                .then((user) => {
                    feedbackContext.showSuccess(
                        translate('users.security.confirmed_2fa') as string
                    )
                    cleanForm(user)
                })
                .catch((error) => feedbackContext.showError(error.message))
                .finally(feedbackContext.closeBackdrop)
        },
        [isValid, bind, cleanForm, feedbackContext]
    )

    const onDisable2FA = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            if (!isValid()) {
                return
            }

            feedbackContext.showBackdrop()

            const request = bind()
            update2FA(request)
                .then((user) => {
                    feedbackContext.showSuccess(
                        translate('users.security.disabled_2fa') as string
                    )
                    cleanForm(user)
                })
                .catch((error) => feedbackContext.showError(error.message))
                .finally(feedbackContext.closeBackdrop)
        },
        [isValid, bind, cleanForm, feedbackContext]
    )

    const cancel2FA = useCallback(() => {
        feedbackContext.showBackdrop()
        update2FA()
            .then((user) => {
                feedbackContext.showSuccess(
                    translate('users.security.disabled_2fa') as string
                )
                cleanForm(user)
            })
            .catch((error) => feedbackContext.showError(error.message))
            .finally(feedbackContext.closeBackdrop)
    }, [cleanForm, feedbackContext])

    return (
        <Surface
            title={translate('users.security.2fa') as string}
            icon={<TwoFALockIcon />}
        >
            <Grid
                container
                alignItems="center"
                justifyContent="center"
                alignContent="center"
            >
                <Grid item xs={12}>
                    <Grid
                        container
                        spacing={3}
                        alignContent="center"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {user.status_2fa === 'DISABLED' && (
                            <Grid item xs={12}>
                                <Grid container>
                                    <Grid item xs={12}>
                                        <Typography variant="caption">
                                            {translate(
                                                'users.security.2fa_status.DISABLED'
                                            )}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box my={2}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                size="large"
                                                onClick={onEnable2FA}
                                            >
                                                {translate(
                                                    'users.security.buttons.enable_2fa'
                                                )}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                        {user.status_2fa === 'NOT_VERIFIED' && (
                            <Grid item xs={12}>
                                <Grid
                                    container
                                    spacing={3}
                                    alignItems="flex-start"
                                    alignContent="center"
                                    justifyContent="center"
                                >
                                    <Grid item xs="auto">
                                        <img
                                            src={user.qr_url_2fa!}
                                            alt="2FA QR"
                                        ></img>
                                    </Grid>
                                    <Grid item xs={12} md>
                                        <Typography variant="caption">
                                            {translate(
                                                'users.security.2fa_status.NOT_VERIFIED',
                                                { secret: user.secret_2fa }
                                            )}
                                        </Typography>
                                        <form
                                            autoComplete="off"
                                            noValidate
                                            onSubmit={onConfirm2FA}
                                        >
                                            <Grid
                                                container
                                                alignItems="center"
                                                alignContent="center"
                                                justifyContent="center"
                                            >
                                                <Grid item xs={12}>
                                                    <Box mt={2}>
                                                        <AnyInput
                                                            type="text"
                                                            name="code"
                                                            label={
                                                                translate(
                                                                    'users.2fa_code'
                                                                ) as string
                                                            }
                                                            maxLength={6}
                                                            required
                                                            register={register}
                                                            validators={
                                                                validators
                                                            }
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box my={2}>
                                                        <Button
                                                            type="submit"
                                                            variant="contained"
                                                            color="primary"
                                                            fullWidth
                                                            size="large"
                                                        >
                                                            {translate(
                                                                'users.security.buttons.enable_2fa'
                                                            )}
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Button
                                                        type="button"
                                                        variant="outlined"
                                                        color="primary"
                                                        fullWidth
                                                        size="large"
                                                        onClick={cancel2FA}
                                                    >
                                                        {translate(
                                                            'users.security.buttons.disable_2fa'
                                                        )}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </form>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                        {user.status_2fa === 'ENABLED' && (
                            <Grid item xs={12}>
                                <form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={onDisable2FA}
                                >
                                    <Grid
                                        container
                                        alignItems="center"
                                        alignContent="center"
                                        justifyContent="center"
                                    >
                                        <Grid item xs={12}>
                                            <Typography variant="caption">
                                                {translate(
                                                    'users.security.2fa_status.ENABLED'
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <AnyInput
                                                type="text"
                                                name="code"
                                                label={
                                                    translate(
                                                        'users.2fa_code'
                                                    ) as string
                                                }
                                                maxLength={6}
                                                register={register}
                                                required
                                                validators={validators}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box my={2}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="primary"
                                                    fullWidth
                                                    size="large"
                                                >
                                                    {translate(
                                                        'users.security.buttons.disable_2fa'
                                                    )}
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Surface>
    )
}
